import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { CheckCircle2, AlertTriangle } from 'lucide-react';

import { getApprovalSummary } from '../../services/approvalsService';
import StatTileCard from '../../components/ui/StatTileCard';
import { APPROVAL_CATEGORIES } from './config/categories';
import { APPROVALS_COUNT_EVENT } from '../../hooks/approvalsBadge';

// Onaylarım paneli — eskiden sekiz ayrı rotaya dağılmış "Onaylarım" menü grubunun
// yerini alan tek panel sayfası (bkz. spec 2026-08-05-onaylar-paneli). Sunucudan
// GELEN kategori listesi TEK doğruluk kaynağıdır: kullanıcının modülü/yetkisi
// olmayan kategori yanıtta hiç yer almaz, bu yüzden sayfa kendi başına ikinci bir
// gate mantığı İCAT ETMEZ — yalnız sunucunun döndürdüğünü kayıt defteri sırasında
// çizer. Rota da (routes/index.js) bilinçli olarak `privileges` taşımaz.

// Sunucu {key, count} döndürür; sıra ve sunum kayıt defterinden gelir.
// Sunucuda OLMAYAN kategori render EDİLMEZ — görünürlükte otorite sunucudur.
// Kayıt defterinde olmayan bir key gelirse (ileride sunucuya kategori eklenip
// frontend güncellenmezse) sessizce atlanır, çökmez.
export function buildCards(serverCategories) {
  const counts = new Map((serverCategories || []).map((c) => [c.key, c.count]));
  return APPROVAL_CATEGORIES
    .filter((c) => counts.has(c.key))
    .map((c) => ({ ...c, count: counts.get(c.key) }));
}

// Panel, kaç kart geleceğini önceden bilemez (tenant'a göre 0-9 arası) — flex-wrap
// tabanlı yerleşim, dashboard bölümlerinde de kullanılan aynı çözümdür
// (pages/dashboard/styles.js'teki SectionGrid yorumuna bakınız): CSS grid'in
// `auto-fit`'i yalnız hiç kart almayan sütunları toplar, kısmen dolu SON satırı
// düzeltmez; flex'te satırın boş alanı o satırdaki kartlara dağıtılır.
const Grid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;

  > * {
    flex: 1 1 240px;
    min-width: min(100%, 240px);
  }
`;

const Container = styled.div``;

const PageHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 18px;
`;

const PageTitle = styled.h2`
  margin: 0;
  font-size: ${({ theme }) => theme.metrics.font.size.xl};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const CountChip = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 4px 12px;
  border-radius: 14px;
  font-size: ${({ theme }) => theme.metrics.font.size.xs};
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  color: ${({ theme }) => theme.colors.primary.dark};
  background: ${({ theme }) => `${theme.colors.primary.main}1A`};
`;

const Card = styled.div`
  background: ${({ theme }) => theme.colors.surface?.card || '#fff'};
  border: 1px solid ${({ theme }) => theme.colors.surface?.border || '#E8ECF0'};
  border-radius: 10px;
  padding: 20px 22px;
`;

const Centered = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 60px 24px;
  text-align: center;
`;

const EmptyIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  border-radius: 50%;
  color: ${({ theme }) => theme.colors.primary.main};
  background: ${({ theme }) => `${theme.colors.primary.main}1A`};
`;

// "Yüklenemedi" ile "kategorin yok" GÖRSEL OLARAK da ayrışsın diye ayrı renk —
// ikisi aynı yeşil tik ile gösterilirse kullanıcı "her şey yolunda" okur.
const ErrorIcon = styled(EmptyIcon)`
  color: ${({ theme }) => theme.colors.auxiliar.danger};
  background: ${({ theme }) => `${theme.colors.auxiliar.danger}1A`};
`;

const EmptyText = styled.div`
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  color: ${({ theme }) => theme.colors.text.dark.little};
  max-width: 420px;
`;

export default function ApprovalsHubPage() {
  const { t } = useTranslation();
  const history = useHistory();
  const [serverCategories, setServerCategories] = useState(null);
  const [total, setTotal] = useState(0);
  // 'loading' | 'ready' | 'unavailable' — "veri yok" (gerçekten sıfır kategori)
  // ile "yüklenemedi" (ağ/500 hatası) AYNI EKRANA düşemez: bu paneldeki tek işin
  // bekleyen işi görünür kılmak olduğu düşünülürse, "her şey yolunda" yanlış
  // pozitifi "hiç bilgi yok" demekten daha kötüdür. Desen
  // HrAnalyticsInsightsPage.js'teki narrativeState ile aynı.
  //
  // Ayırt edici sinyal `res.data.status`'tur, promise reddi (.catch) DEĞİL:
  // services/api.js'in interceptor'ı OKUMA (GET) isteklerinde asla reddetmez
  // (bkz. api.js "Reads → resolve empty" yorumu) — 500/ağ hatası bile
  // `{status:false, data:null}` ile ÇÖZÜLÜR. Bu yüzden `.catch()` yalnız
  // savunma amaçlı kalır; asıl kapı `res.data?.status` kontrolüdür.
  const [status, setStatus] = useState('loading');

  useEffect(() => {
    let cancelled = false;
    setStatus('loading');
    getApprovalSummary().then((res) => {
      if (cancelled) return;
      if (!res.data?.status) {
        setStatus('unavailable');
        return;
      }
      const data = res.data.data || {};
      const categories = data.categories || [];
      // Sunucunun kendi `total` alanı — kayıt defterinin render EDEBİLDİĞİ
      // kartların toplamı DEĞİL. Aynı sayı hem sayfanın üst çipinde hem de
      // rozet olayında kullanılır ki ikisi HİÇBİR ZAMAN ayrışmasın.
      const exactTotal = typeof data.total === 'number' ? data.total : 0;
      setServerCategories(categories);
      setTotal(exactTotal);
      setStatus('ready');

      // Sol menü rozeti 60 sn önbellekli sayaç ucundan beslenir; sayfa
      // buradayken kesin toplam BUDUR (bkz. hooks/useApprovalsBadge, Görev 7).
      // Yalnız BAŞARILI yanıtta yayınlanır — bilinmeyen bir sayıyı rozete
      // "0" olarak yansıtmak yanlış bilgi yaymak olurdu. Olay adı
      // hooks/approvalsBadge.js'ten İTHAL EDİLİR (literal DEĞİL) — rozetin
      // dinlediği isimle bu sayfanın yaydığı isim TEK kaynaktan gelsin,
      // ikisi ayrı dosyada aynı string'i taşıyıp bir rename'de sessizce
      // ayrışmasın (bkz. Görev 7 inceleme bulgusu).
      window.dispatchEvent(new CustomEvent(APPROVALS_COUNT_EVENT, { detail: { count: exactTotal } }));
    }).catch(() => {
      if (!cancelled) setStatus('unavailable');
    });
    return () => { cancelled = true; };
  }, []);

  const cards = status === 'ready' ? buildCards(serverCategories) : [];

  const goTo = useCallback((path) => history.push(path), [history]);

  return (
    <Container>
      <PageHeader>
        <PageTitle>{t('approvals.title')}</PageTitle>
        {status === 'ready' && cards.length > 0 && (
          <CountChip>{t('approvals.total', { count: total })}</CountChip>
        )}
      </PageHeader>

      {status === 'loading' && (
        <Card><Centered><EmptyText>{t('common.loading')}</EmptyText></Centered></Card>
      )}

      {status === 'unavailable' && (
        <Card>
          <Centered>
            <ErrorIcon><AlertTriangle size={26} /></ErrorIcon>
            <EmptyText>{t('approvals.loadFailed')}</EmptyText>
          </Centered>
        </Card>
      )}

      {status === 'ready' && cards.length === 0 && (
        <Card>
          <Centered>
            <EmptyIcon><CheckCircle2 size={26} /></EmptyIcon>
            <EmptyText>{t('approvals.empty')}</EmptyText>
          </Centered>
        </Card>
      )}

      {status === 'ready' && cards.length > 0 && (
        <Grid>
          {cards.map((c) => (
            <StatTileCard
              key={c.key}
              icon={c.icon}
              value={c.count}
              label={t(c.labelKey)}
              sublabel={c.count === 0 ? t('approvals.nonePending') : t('approvals.pendingCount', { count: c.count })}
              muted={c.count === 0}
              onClick={() => goTo(c.path)}
            />
          ))}
        </Grid>
      )}
    </Container>
  );
}
